'use client'

import { useMemo } from 'react'
import { experiments, getExperimentCookieName } from './config'

/**
 * Hook to get the current variant for an experiment
 *
 * @param {string} experimentId - The experiment ID from config
 * @returns {string|null} The variant name, or null if experiment not found/inactive
 *
 * Example:
 *   const variant = useExperiment('homepage-headline')
 *   if (variant === 'control') return <ControlVersion />
 *   if (variant === 'v1') return <VariantOne />
 */
export function useExperiment(experimentId) {
  return useMemo(() => {
    const experiment = experiments[experimentId]

    if (!experiment || !experiment.active) {
      return null
    }

    // Read variant from cookie
    if (typeof document === 'undefined') {
      return experiment.variants[0] // SSR fallback to control
    }

    const cookieName = getExperimentCookieName(experimentId)
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=')
      acc[key] = value
      return acc
    }, {})

    const variant = cookies[cookieName]

    // Return variant if valid, otherwise fallback to control
    if (variant && experiment.variants.includes(variant)) {
      return variant
    }

    return experiment.variants[0]
  }, [experimentId])
}

/**
 * Get all active experiment assignments for the current user
 * Useful for analytics and debugging
 *
 * @returns {Object} Map of experimentId -> variant
 */
export function useExperimentAssignments() {
  return useMemo(() => {
    if (typeof document === 'undefined') {
      return {}
    }

    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=')
      acc[key] = value
      return acc
    }, {})

    const assignments = {}

    Object.values(experiments).forEach((experiment) => {
      if (!experiment.active) return

      const cookieName = getExperimentCookieName(experiment.id)
      const variant = cookies[cookieName]

      if (variant && experiment.variants.includes(variant)) {
        assignments[experiment.id] = variant
      }
    })

    return assignments
  }, [])
}
